import { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface AgendaEvent {
  id: string; title: string; startTime: string; endTime: string;
  maxCapacity: number; price?: number; location?: string; image?: string;
  _count?: { bookings: number };
  bookings?: { id: string; status: string; client: { firstName: string; lastName: string; email: string; phone?: string } }[];
}

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const STATUS_COLORS: Record<string, string> = {
  CONFIRMED: '#10b981', PENDING: '#f59e0b', CANCELLED: '#ef4444', COMPLETED: '#3b82f6'
};

export function Agenda() {
  const [view, setView] = useState<'calendario'|'lista'>('calendario');
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<AgendaEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (selectedBiz) fetchEvents(); }, [selectedBiz, currentDate]);

  const fetchData = async () => {
    try {
      const r = await businessApi.getMyBusinesses();
      const list = r.data.data.businesses || [];
      setBusinesses(list);
      if (list.length > 0) { setSelectedBiz(list[0].id); }
    } catch { toast.error('Error cargando datos'); }
    finally { setLoading(false); }
  };

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const from = new Date(year, month, 1).toISOString();
      const to = new Date(year, month + 1, 0, 23, 59).toISOString();
      const r = await axios.get(`${API_URL}/events/business/${selectedBiz}?from=${from}&to=${to}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(r.data.data.events || []);
    } catch {}
  };

  const fetchEventBookings = async (eventId: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      const r = await axios.get(`${API_URL}/bookings/business/${selectedBiz}?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allBookings = r.data.data.bookings || [];
      const eventBookings = allBookings.filter((b: any) => b.eventId === eventId);
      setSelectedEvent(prev => prev ? { ...prev, bookings: eventBookings } : null);
    } catch {}
  };

  const prevMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  // Calcular días del calendario
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;

  const getEventsForDay = (day: number) => {
    return events.filter(e => {
      const d = new Date(e.startTime);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
  };

  const dayEvents = selectedDay ? getEventsForDay(selectedDay) : [];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-5">
      <style>{`
        .ag-cal { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
        .ag-day-header { text-align: center; font-size: .72rem; font-weight: 700; color: #64748b; padding: 8px 0; text-transform: uppercase; letter-spacing: .06em; }
        .ag-day { min-height: 80px; background: white; border: 1px solid #f1f5f9; border-radius: 8px; padding: 6px; cursor: pointer; transition: all .2s; position: relative; }
        .ag-day:hover { border-color: #3b82f6; background: #eff6ff; }
        .ag-day.today { border-color: #3b82f6; background: #eff6ff; }
        .ag-day.selected { border-color: #3b82f6; background: #dbeafe; }
        .ag-day.other-month { background: #f8fafc; opacity: .5; }
        .ag-day.has-events { border-color: #e2e8f0; }
        .ag-day-num { font-size: .82rem; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
        .ag-day.today .ag-day-num { color: #2563eb; }
        .ag-ev-dot { font-size: .65rem; font-weight: 600; padding: 2px 6px; border-radius: 4px; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; background: #dbeafe; color: #1d4ed8; }
        .ag-ev-dot.full { background: #fee2e2; color: #dc2626; }
        .ev-row { background: white; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin-bottom: 10px; cursor: pointer; transition: all .2s; }
        .ev-row:hover { border-color: #3b82f6; box-shadow: 0 4px 12px rgba(59,130,246,.1); }
        .client-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f1f5f9; }
        .client-avatar { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; font-size: .82rem; flex-shrink: 0; }
        .modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 20px; backdrop-filter: blur(4px); }
        .modal-box { background: white; border-radius: 20px; padding: 28px; max-width: 500px; width: 100%; max-height: 85vh; overflow-y: auto; box-shadow: 0 40px 100px rgba(0,0,0,.2); }
      `}</style>

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">📅 Agenda</h1>
          <p className="text-secondary-500 text-sm mt-1">Vista completa de tus eventos e inscripciones</p>
        </div>
        <div className="flex items-center gap-3">
          {businesses.length > 1 && (
            <select className="input" value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)}>
              {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 4, gap: 4 }}>
            <button onClick={() => setView('calendario')} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: '.82rem', cursor: 'pointer', fontFamily: 'inherit', background: view==='calendario'?'white':'transparent', color: view==='calendario'?'#1e293b':'#64748b', boxShadow: view==='calendario'?'0 1px 3px rgba(0,0,0,.1)':'none', transition: 'all .2s' }}>
              📆 Calendario
            </button>
            <button onClick={() => setView('lista')} style={{ padding: '7px 16px', borderRadius: 8, border: 'none', fontWeight: 600, fontSize: '.82rem', cursor: 'pointer', fontFamily: 'inherit', background: view==='lista'?'white':'transparent', color: view==='lista'?'#1e293b':'#64748b', boxShadow: view==='lista'?'0 1px 3px rgba(0,0,0,.1)':'none', transition: 'all .2s' }}>
              📋 Lista
            </button>
          </div>
        </div>
      </div>

      {/* NAVEGACIÓN MES */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'white', borderRadius: 14, padding: '14px 20px', border: '1px solid #e2e8f0' }}>
        <button onClick={prevMonth} style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>←</button>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>{MONTHS[month]} {year}</p>
          <p style={{ fontSize: '.72rem', color: '#64748b' }}>{events.length} eventos este mes</p>
        </div>
        <button onClick={nextMonth} style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>→</button>
      </div>

      {/* VISTA CALENDARIO */}
      {view === 'calendario' && (
        <div>
          <div className="card p-4">
            <div className="ag-cal" style={{ marginBottom: 8 }}>
              {DAYS.map(d => <div key={d} className="ag-day-header">{d}</div>)}
            </div>
            <div className="ag-cal">
              {Array.from({ length: startOffset }, (_, i) => (
                <div key={`empty-${i}`} className="ag-day other-month"></div>
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dayEvs = getEventsForDay(day);
                const today = new Date();
                const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
                return (
                  <div key={day} className={`ag-day ${isToday?'today':''} ${selectedDay===day?'selected':''} ${dayEvs.length>0?'has-events':''}`}
                    onClick={() => setSelectedDay(selectedDay === day ? null : day)}>
                    <div className="ag-day-num">{day}</div>
                    {dayEvs.slice(0, 2).map(e => {
                      const spots = e.maxCapacity - (e._count?.bookings || 0);
                      return (
                        <div key={e.id} className={`ag-ev-dot ${spots <= 0 ? 'full' : ''}`}>
                          {new Date(e.startTime).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/Madrid'})} {e.title}
                        </div>
                      );
                    })}
                    {dayEvs.length > 2 && <div style={{ fontSize: '.6rem', color: '#94a3b8', marginTop: 2 }}>+{dayEvs.length-2} más</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Eventos del día seleccionado */}
          {selectedDay && dayEvents.length > 0 && (
            <div className="card p-5">
              <h3 style={{ fontWeight: 700, fontSize: '.95rem', color: '#1e293b', marginBottom: 14 }}>
                📅 {selectedDay} de {MONTHS[month]} — {dayEvents.length} evento{dayEvents.length!==1?'s':''}
              </h3>
              {dayEvents.map(e => {
                const spots = e.maxCapacity - (e._count?.bookings || 0);
                return (
                  <div key={e.id} className="ev-row" onClick={() => { setSelectedEvent(e); fetchEventBookings(e.id); }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: '.92rem', color: '#1e293b' }}>{e.title}</p>
                        <p style={{ fontSize: '.75rem', color: '#64748b', marginTop: 2 }}>
                          🕐 {new Date(e.startTime).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/Madrid'})} – {new Date(e.endTime).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/Madrid'})}
                          {e.location && ` · 📍 ${e.location}`}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '.82rem', fontWeight: 700, color: spots<=0?'#dc2626':spots<=3?'#f59e0b':'#10b981' }}>
                          {spots<=0?'Completo':`${e._count?.bookings||0}/${e.maxCapacity} inscritos`}
                        </p>
                        <p style={{ fontSize: '.72rem', color: '#94a3b8' }}>Ver inscritos →</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedDay && dayEvents.length === 0 && (
            <div className="card p-8 text-center">
              <p style={{ color: '#94a3b8', fontSize: '.88rem' }}>Sin eventos el {selectedDay} de {MONTHS[month]}</p>
              <a href="/events" style={{ color: '#3b82f6', fontSize: '.82rem', fontWeight: 600, textDecoration: 'none' }}>+ Crear evento →</a>
            </div>
          )}
        </div>
      )}

      {/* VISTA LISTA */}
      {view === 'lista' && (
        <div>
          {events.length > 0 ? events.map(e => {
            const d = new Date(e.startTime);
            const spots = e.maxCapacity - (e._count?.bookings || 0);
            const ocupacion = Math.round(((e._count?.bookings||0) / e.maxCapacity) * 100);
            return (
              <div key={e.id} className="ev-row" onClick={() => { setSelectedEvent(e); fetchEventBookings(e.id); }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  {/* Fecha */}
                  <div style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: 'white', borderRadius: 12, padding: '12px 14px', textAlign: 'center', minWidth: 58, flexShrink: 0 }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 900, lineHeight: 1 }}>{d.getDate()}</div>
                    <div style={{ fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', opacity: .85 }}>{MONTHS[d.getMonth()].slice(0,3)}</div>
                  </div>
                  {/* Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <p style={{ fontWeight: 800, fontSize: '.95rem', color: '#1e293b' }}>{e.title}</p>
                      <span style={{ fontSize: '.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 100, background: spots<=0?'#fee2e2':spots<=3?'#fef9c3':'#dcfce7', color: spots<=0?'#dc2626':spots<=3?'#854d0e':'#15803d' }}>
                        {spots<=0?'Completo':spots<=3?`⚡ ${spots} plazas`:`${e._count?.bookings||0}/${e.maxCapacity}`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 14, fontSize: '.75rem', color: '#64748b', marginBottom: 10, flexWrap: 'wrap' }}>
                      <span>🕐 {d.toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/Madrid'})}</span>
                      {e.location && <span>📍 {e.location}</span>}
                      {e.price && Number(e.price)>0 ? <span>💶 {e.price}€</span> : <span>🆓 Gratis</span>}
                    </div>
                    {/* Barra ocupación */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.68rem', color: '#94a3b8', marginBottom: 3 }}>
                        <span>Ocupación</span>
                        <span style={{ fontWeight: 700, color: ocupacion>=80?'#10b981':ocupacion>=50?'#f59e0b':'#64748b' }}>{ocupacion}%</span>
                      </div>
                      <div style={{ background: '#f1f5f9', borderRadius: 100, height: 5 }}>
                        <div style={{ height: 5, borderRadius: 100, width: `${Math.min(ocupacion,100)}%`, background: ocupacion>=80?'#10b981':ocupacion>=50?'#f59e0b':'#3b82f6', transition: 'width .3s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }) : (
            <div className="card p-12 text-center">
              <div style={{ fontSize: '3rem', marginBottom: 12 }}>📅</div>
              <p className="text-secondary-500 font-medium mb-4">Sin eventos en {MONTHS[month]}</p>
              <a href="/events" className="btn-primary inline-flex items-center gap-2">+ Crear evento</a>
            </div>
          )}
        </div>
      )}

      {/* MODAL DETALLE EVENTO + INSCRITOS */}
      {selectedEvent && (
        <div className="modal-bg" onClick={() => setSelectedEvent(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e293b', marginBottom: 4 }}>{selectedEvent.title}</h2>
                <p style={{ fontSize: '.78rem', color: '#64748b' }}>
                  📅 {new Date(selectedEvent.startTime).toLocaleDateString('es-ES',{weekday:'long',day:'numeric',month:'long',timeZone:'Europe/Madrid'})}
                  · 🕐 {new Date(selectedEvent.startTime).toLocaleTimeString('es-ES',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/Madrid'})}
                </p>
              </div>
              <button onClick={() => setSelectedEvent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.2rem' }}>✕</button>
            </div>

            {/* Stats evento */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                <p style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>{selectedEvent._count?.bookings || 0}</p>
                <p style={{ fontSize: '.68rem', color: '#64748b' }}>Inscritos</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                <p style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>{selectedEvent.maxCapacity - (selectedEvent._count?.bookings || 0)}</p>
                <p style={{ fontSize: '.68rem', color: '#64748b' }}>Disponibles</p>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                <p style={{ fontSize: '1.4rem', fontWeight: 800, color: '#10b981' }}>
                  {selectedEvent.price && Number(selectedEvent.price)>0 ? `€${(Number(selectedEvent.price) * (selectedEvent._count?.bookings||0)).toFixed(0)}` : '—'}
                </p>
                <p style={{ fontSize: '.68rem', color: '#64748b' }}>Ingresos</p>
              </div>
            </div>

            {/* Lista inscritos */}
            <h3 style={{ fontWeight: 700, fontSize: '.85rem', color: '#1e293b', marginBottom: 12 }}>
              👥 Inscritos ({selectedEvent.bookings?.length || 0})
            </h3>
            {selectedEvent.bookings && selectedEvent.bookings.length > 0 ? (
              selectedEvent.bookings.map((b, i) => (
                <div key={i} className="client-row">
                  <div className="client-avatar">
                    {b.client?.firstName?.charAt(0)}{b.client?.lastName?.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 600, fontSize: '.85rem', color: '#1e293b' }}>{b.client?.firstName} {b.client?.lastName}</p>
                    <p style={{ fontSize: '.72rem', color: '#64748b' }}>{b.client?.email}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {b.client?.phone && (
                      <a href={`https://wa.me/${b.client.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                        style={{ width: 32, height: 32, borderRadius: 8, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: '.9rem' }}>
                        💬
                      </a>
                    )}
                    <a href={`mailto:${b.client?.email}`}
                      style={{ width: 32, height: 32, borderRadius: 8, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: '.9rem' }}>
                      ✉️
                    </a>
                    <span style={{ background: STATUS_COLORS[b.status]+'22', color: STATUS_COLORS[b.status], fontSize: '.65rem', fontWeight: 700, padding: '4px 8px', borderRadius: 100 }}>
                      {b.status === 'CONFIRMED' ? 'Confirmado' : b.status === 'PENDING' ? 'Pendiente' : b.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ color: '#94a3b8', fontSize: '.85rem', textAlign: 'center', padding: '20px 0' }}>
                {selectedEvent.bookings === undefined ? 'Cargando...' : 'Sin inscritos todavía'}
              </p>
            )}

            <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
              <a href="/events" style={{ flex: 1, padding: '11px', borderRadius: 10, background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '.85rem', textAlign: 'center', textDecoration: 'none' }}>
                ✏️ Editar evento
              </a>
              <button onClick={() => setSelectedEvent(null)} style={{ flex: 1, padding: '11px', borderRadius: 10, background: '#f1f5f9', color: '#475569', border: 'none', fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
